/* eslint-disable prettier/prettier */
import { useEffect, useState, useCallback } from 'react';
import { assessmentService } from '../services/assessment.service';
import { Assessment, CreateAssessmentPayload } from '../types/assessment';

export function useCourseAssessments(courseId?: string) {
	const [assessments, setAssessments] = useState<Assessment[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	/* ------------------ FETCH ------------------ */
	const fetchAssessments = useCallback(async () => {
		if (!courseId) return;

		setLoading(true);
		setError(null);

		try {
			const data =
				await assessmentService.getAssessmentByCourse(courseId);
			setAssessments(data);
		} catch (err: any) {
			setAssessments([]);
			setError(err.message || 'Failed to load assignments');
		} finally {
			setLoading(false);
		}
	}, [courseId]);

	useEffect(() => {
		fetchAssessments();
	}, [fetchAssessments]);

	/* ------------------ UPDATE ------------------ */
	const updateAssessment = async (
		assessmentId: string,
		data: Partial<CreateAssessmentPayload>
	) => {
		try {
			setLoading(true);

			const updated = await assessmentService.updateAssessment(
				assessmentId,
				data
			);

			// 🔥 Use backend response instead of merging raw payload
			setAssessments(prev =>
				prev.map(a => (a.id === assessmentId ? updated : a))
			);

			return true;
		} catch (err: any) {
			setError(err.message || 'Failed to update assessment');
			return false;
		} finally {
			setLoading(false);
		}
	};

	/* ------------------ DELETE ------------------ */
	const deleteAssessment = async (assessmentId: string) => {
		try {
			setLoading(true);

			await assessmentService.deleteAssessment(assessmentId);

			// 🔥 Optimistic remove (no refetch needed)
			setAssessments(prev => prev.filter(a => a.id !== assessmentId));

			return true;
		} catch (err: any) {
			setError(err.message || 'Failed to delete assessment');
			return false;
		} finally {
			setLoading(false);
		}
	};

	return {
		assessments,
		loading,
		error,
		refetch: fetchAssessments,
		updateAssessment,
		deleteAssessment,
	};
}
